import React, { useMemo } from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  LabelList,
  Cell,
} from "recharts";
import { BarChart3 } from "lucide-react";

const loanColors = {
  "Business Loan": "#00B9AD",
  "Emergency Loan": "#004D4D",
  "Flash Loan": "#00C400",
  "Normal Loan": "#FFD23F",
  "Development Loan": "#00B9AD",
  "Checkoff Loan": "#003F87",
};

// 🔹 Helper
const hasValidData = (items) => {
  if (!Array?.isArray(items) || items?.length === 0) return false;

  return items?.some(
    (item) =>
      typeof item?.avgTenureYears === "number" &&
      !Number?.isNaN(item?.avgTenureYears) &&
      item?.avgTenureYears > 0,
  );
};

const LoanTenure = ({ apiData }) => {
  // ✅ Hooks FIRST — no conditions
  const chartData = useMemo(() => {
    if (!hasValidData(apiData?.items)) return [];

    return [...apiData?.items]
      ?.map((item) => ({
        name: item?.loanType,
        value: Number(item?.avgTenureYears.toFixed(2)),
        fill: loanColors[item?.loanType] || "#8884d8",
      }))
      ?.sort((a, b) => a?.value - b?.value);
  }, [apiData]);

  const maxYears = useMemo(() => {
    if (!chartData?.length) return 1;
    return Math?.ceil(Math?.max(...chartData?.map((d) => d?.value)));
  }, [chartData]);

  const ticks = useMemo(
    () => Array?.from({ length: maxYears }, (_, i) => i + 1),
    [maxYears],
  );

  const hasData = chartData?.length > 0;

  // ⛔ Early return AFTER hooks (now safe)
  if (!hasData) {
    return (
      <div className="w-full h-[350px] flex flex-col items-center justify-center border border-gray-200 rounded-lg bg-gray-50">
        <BarChart3 className="w-10 h-10 text-[#074043] mb-2" />
        <p className="text-[#074043] text-lg">No data available</p>
      </div>
    );
  }

  // ✅ Normal render
  return (
    <BarChart
      width={600}
      height={350}
      data={chartData}
      layout="vertical"
      margin={{ top: 5, right: 90, left: 0, bottom: 5 }}
    >
      <XAxis
        type="number"
        domain={[0, maxYears + 0.5]}
        ticks={ticks}
        axisLine
        tickLine={false}
        tickFormatter={(tick) => `${tick} year${tick > 1 ? "s" : ""}`}
      />

      <YAxis
        type="category"
        dataKey="name"
        tick={false}
        axisLine={{ stroke: "#000", strokeWidth: 1 }}
      />

      <Tooltip />

      <Bar dataKey="value">
        {chartData?.map((entry, index) => (
          <Cell key={`cell-${index}`} fill={entry?.fill} />
        ))}

        <LabelList
          dataKey="name"
          position="right"
          style={{
            fill: "#000",
            fontSize: 12,
            whiteSpace: "nowrap",
          }}
        />
      </Bar>
    </BarChart>
  );
};

export default LoanTenure;